import { FC, useCallback, useEffect } from "react";
import { useRouter } from "next/router";
import { mergeDeepRight } from "ramda";
import { customColorModeManager } from "@smile2impress/themes";
import { Box, ThemeProvider } from "@smile2impress/components";
import { Container } from "@smile2impress/components/relaunch";
import {
    CookiesBanner,
    CookiesBannerContextProvider,
} from "@smile2impress/cookies-banner";
import { useTranslation } from "next-i18next";
import { setTag } from "@sentry/nextjs";
import {
    setCookieByObjectValues,
    getCookiesOrigin,
} from "@smile2impress/attraction-utils";
import {
    UTSLocale,
    UTSLocaleToCountryVersion,
} from "@smile2impress/attraction-constants";
import { ExperimentProvider } from "providers/ExperimentProvider";
import { ExperimentProvider as GrowthbookProvider } from "providers/ExperimentProvider/index";
import { CountdownContextProvider } from "context/countdownContext";
import { useGenerateVidAndSessionId } from "hooks/useGenerateVidAndSessionId";
import { useCountdown } from "hooks/useCountdown";
import { UTSLocaleContext } from "context/utsLocaleContext";
import { ErrorContextProvider } from "context/errorContext";
import { ErrorLayout } from "layouts/relaunch/pages/ErrorLayout";
import { LocationProvider } from "providers/LocationProvider";
import { RecaptchaProvider } from "providers/RecaptchaProvider";
import { IdProvider } from "hooks/useId";
import { SMSVerificationRequiredSetUpType } from "hooks/useIsSmsVerificationRequired";
import { CampaignProvider } from "providers/CampaignProvider";

import {
    SHOW_COOKIES_BANNER_EXCEPTIONS,
    SHOW_LANGUAGE_QUESTION_EXCEPTIONS,
    PAGES_WITHOUT_REGION_SERVICE,
    I18nLocale,
    COOKIES,
    TwentyFourHoursInSeconds,
} from "constants/enum";
import errorContent from "content/error";
import { MetaTags } from "components/MetaTags";
import { Footer } from "components/Relaunch/Footer";
import { getUTSLocale } from "utils/locale";
import { useSendStartAnalitic } from "services/AnaliticsService/useSendStartAnalitic";
import { useForceHtmlLangAttribute } from "services/ForceLangService/useForceHtmlLangAttribute";
import { setVisitorTrajectoryCookie } from "services/VisitorTrajectoryService/helpers";
import { getIDSTheme } from "utils/getIDSTheme";
import { HeadScripts } from "components/HeadScripts";
import { Header } from "components/Relaunch/Header";
import { getAppMode } from "utils/getAppMode";
import { convertQueryStringToQueryObject } from "utils/queryString";
import { MessengerWidget } from "components/Relaunch/MessengerWidget";
import { Popups } from "components/Popups";
import { UrlsConfig } from "configs/urlsConfig";
import smileVisionOfferContent from "content/relaunch/smileVisionOffer";

import { dictionary } from "./index";
import { BaseLayoutProps } from "./RelaunchLayout.types";
import { getPlatformRelatedValue } from "./lib";

const BaseLayout: FC<BaseLayoutProps> = ({
    children,
    themeType,
    i18n,
    hideMenu = false,
    hideFooterMenu = false,
    logoAsLink = true,
    withHeader = true,
    withFixedHeader = true,
    withFixedBanner = true,
    withFooter = true,
    isErrorPage = false,
    withExpandableMobileMenu = true,
    withExpandableMessengerButton = false,
    withMessengerWidget = false,
    layout = {},
    seoTitle,
    seoDescription,
    seo,
    noFooterMarginTop = false,
    promoCampaigns,
    popups,
}) => {
    const { isLiveMode } = getAppMode();
    const router = useRouter();
    const { pathname, route, asPath, locale: routerLocale } = router;
    const cookiesOrigin = getCookiesOrigin();

    const { t } = useTranslation("global");
    const theme = getIDSTheme(themeType);

    const language = i18n?.language || I18nLocale.SPAIN_ES;
    const locale: UTSLocale = getUTSLocale(language);
    const version = UTSLocaleToCountryVersion[locale];

    const { countdownValue, setCountdownValue } = useCountdown();

    // For CMS pages served via the `[[...slug]]` catch-all, `router.pathname` is
    // the route pattern (`/[[...slug]]`), not the URL, so match the exceptions
    // against the localized `asPath` too.
    const currentAsPath = asPath.split(/[?#]/)[0];
    const hideCookiesPolicyBanner =
        SHOW_COOKIES_BANNER_EXCEPTIONS.includes(pathname) ||
        SHOW_COOKIES_BANNER_EXCEPTIONS.includes(currentAsPath);

    const isSmileVisionOfferPage = pathname.startsWith(
        UrlsConfig.SMILE_VISION_OFFER
    );

    const hideLanguageQuestion =
        SHOW_LANGUAGE_QUESTION_EXCEPTIONS.includes(pathname);

    const hideRegionSelector = Boolean(
        PAGES_WITHOUT_REGION_SERVICE.find((page) => pathname.includes(page))
    );

    const { contacts, header, footer } = mergeDeepRight(
        dictionary(locale)(t, { locale }),
        layout
    );

    const footerProps = isSmileVisionOfferPage
        ? (() => {
              const smileVisionContent = smileVisionOfferContent(locale)(t, {
                  locale,
              });
              return {
                  ...footer,
                  footnote1: smileVisionContent.footnote1,
                  footnote2: smileVisionContent.footnote2,
              };
          })()
        : footer;

    const handleAnalyticsAccepted = useCallback(() => {
        setVisitorTrajectoryCookie(router);
    }, [router]);

    useGenerateVidAndSessionId();
    useSendStartAnalitic();
    useForceHtmlLangAttribute(locale);

    setTag("locale", locale);

    const query =
        typeof window !== "undefined"
            ? convertQueryStringToQueryObject(window?.location?.search)
            : {};

    useEffect(() => {
        if (Object.entries(query).length) {
            const { sms, "lead-sms": leadSMS } = query;

            if (
                Object.values(SMSVerificationRequiredSetUpType).includes(
                    sms as SMSVerificationRequiredSetUpType
                )
            ) {
                setCookieByObjectValues(
                    {
                        [COOKIES.IS_SMS_VERIFICATION_REQUIRED_SET_UP]: sms,
                    },
                    {
                        "max-age": TwentyFourHoursInSeconds,
                        path: "/",
                        ...(isLiveMode && { domain: cookiesOrigin }),
                    }
                );
            }

            if (
                Object.values(SMSVerificationRequiredSetUpType).includes(
                    leadSMS as SMSVerificationRequiredSetUpType
                )
            ) {
                setCookieByObjectValues(
                    {
                        [COOKIES.IS_LEAD_SMS_VERIFICATION_REQUIRED_SET_UP]:
                            leadSMS,
                    },
                    {
                        "max-age": TwentyFourHoursInSeconds,
                        path: "/",
                        ...(isLiveMode && { domain: cookiesOrigin }),
                    }
                );
            }
        }
    }, [query]);

    const {
        mobile: withMessengerWidgetForMobile,
        desktop: withMessengerWidgetForDesktop,
    } = getPlatformRelatedValue(withMessengerWidget);

    return (
        <IdProvider>
            <ThemeProvider
                theme={theme}
                cssVarsRoot="body"
                colorModeManager={customColorModeManager(theme)}
            >
                <GrowthbookProvider
                    locale={routerLocale}
                    fullPath={asPath}
                    route={route}
                >
                    <ExperimentProvider locale={locale}>
                        <CountdownContextProvider
                            value={{ countdownValue, setCountdownValue }}
                        >
                            <LocationProvider version={version}>
                                <UTSLocaleContext.Provider value={{ locale }}>
                                    <HeadScripts />
                                    <RecaptchaProvider>
                                        <CookiesBannerContextProvider>
                                            <Popups cmsPopups={popups} />
                                            <CampaignProvider
                                                {...(promoCampaigns && {
                                                    campaigns: promoCampaigns,
                                                })}
                                            >
                                                <ErrorContextProvider>
                                                    {(errorType) => (
                                                        <Box
                                                            backgroundColor="bg.base"
                                                            overflow="hidden"
                                                            position="relative"
                                                        >
                                                            <MetaTags
                                                                title={seoTitle}
                                                                description={
                                                                    seoDescription
                                                                }
                                                                seo={seo}
                                                            />
                                                            {errorType ? (
                                                                <ErrorLayout
                                                                    content={errorContent[
                                                                        locale
                                                                    ](t)}
                                                                    type={
                                                                        errorType
                                                                    }
                                                                />
                                                            ) : (
                                                                <>
                                                                    {withHeader &&
                                                                        header.withHeader !==
                                                                            false && (
                                                                            <Header
                                                                                isErrorPage={
                                                                                    isErrorPage
                                                                                }
                                                                                hideRegionSelector={
                                                                                    hideRegionSelector
                                                                                }
                                                                                hideLanguageQuestion={
                                                                                    hideLanguageQuestion
                                                                                }
                                                                                expandableMobileMenu={
                                                                                    withExpandableMobileMenu
                                                                                }
                                                                                expandableMessengerButton={
                                                                                    withExpandableMessengerButton
                                                                                }
                                                                                content={{
                                                                                    ...header,
                                                                                    contacts,
                                                                                }}
                                                                                isFixed={
                                                                                    withFixedHeader
                                                                                }
                                                                                hasFixedBanner={
                                                                                    withFixedBanner
                                                                                }
                                                                                hideMenu={
                                                                                    hideMenu
                                                                                }
                                                                                logoAsLink={
                                                                                    logoAsLink
                                                                                }
                                                                            />
                                                                        )}
                                                                    <Box>
                                                                        {
                                                                            children
                                                                        }
                                                                    </Box>
                                                                    {withFooter &&
                                                                        footer.withFooter !==
                                                                            false && (
                                                                            <Container
                                                                                variant="noMobilePX"
                                                                                mb={[
                                                                                    0,
                                                                                    0,
                                                                                    2,
                                                                                ]}
                                                                                {...(!noFooterMarginTop
                                                                                    ? {
                                                                                          mt: 4,
                                                                                      }
                                                                                    : {})}
                                                                            >
                                                                                <Footer
                                                                                    {...footerProps}
                                                                                    hideFooterMenu={
                                                                                        hideFooterMenu
                                                                                    }
                                                                                    theme={
                                                                                        theme
                                                                                    }
                                                                                    containerProps={
                                                                                        isSmileVisionOfferPage
                                                                                            ? {
                                                                                                  pb: [
                                                                                                      6,
                                                                                                      6,
                                                                                                      5,
                                                                                                  ],
                                                                                              }
                                                                                            : undefined
                                                                                    }
                                                                                />
                                                                            </Container>
                                                                        )}
                                                                    {contacts &&
                                                                        !isSmileVisionOfferPage && (
                                                                            <Box
                                                                                display={[
                                                                                    withMessengerWidgetForMobile
                                                                                        ? "block"
                                                                                        : "none",
                                                                                    withMessengerWidgetForMobile
                                                                                        ? "block"
                                                                                        : "none",
                                                                                    withMessengerWidgetForDesktop
                                                                                        ? "block"
                                                                                        : "none",
                                                                                ]}
                                                                            >
                                                                                <MessengerWidget
                                                                                    expandableMessengerButton={
                                                                                        withExpandableMessengerButton
                                                                                    }
                                                                                />
                                                                            </Box>
                                                                        )}
                                                                    <CookiesBanner
                                                                        locale={
                                                                            locale
                                                                        }
                                                                        // TODO: AD-686
                                                                        // @ts-ignore
                                                                        i18n={{
                                                                            i18n,
                                                                        }}
                                                                        urlsConfig={{
                                                                            website:
                                                                                "",
                                                                        }}
                                                                        cookieConfig={
                                                                            isLiveMode
                                                                                ? {
                                                                                      domain: cookiesOrigin,
                                                                                  }
                                                                                : undefined
                                                                        }
                                                                        hideCookiesPolicyBanner={
                                                                            hideCookiesPolicyBanner
                                                                        }
                                                                        onAnalyticsAccepted={
                                                                            handleAnalyticsAccepted
                                                                        }
                                                                    />
                                                                </>
                                                            )}
                                                        </Box>
                                                    )}
                                                </ErrorContextProvider>
                                            </CampaignProvider>
                                        </CookiesBannerContextProvider>
                                    </RecaptchaProvider>
                                </UTSLocaleContext.Provider>
                            </LocationProvider>
                        </CountdownContextProvider>
                    </ExperimentProvider>
                </GrowthbookProvider>
            </ThemeProvider>
        </IdProvider>
    );
};

export default BaseLayout;
